import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Printer, LogOut, Phone, MessageCircle, Download, FileText, Loader2,
  Inbox, TrendingUp, RefreshCw, Search, FileSpreadsheet, Images, Pencil, Sparkles, ShoppingBag, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE } from "@/lib/api";
import { waLink } from "@/data/business";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUSES = ["All", "New", "Contacted", "Quoted", "In Production", "Completed"];
const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700",
  Contacted: "bg-amber-100 text-amber-700",
  Quoted: "bg-purple-100 text-purple-700",
  "In Production": "bg-orange-100 text-orange-700",
  Completed: "bg-green-100 text-green-700",
};

export default function AdminDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/leads", { params: filter !== "All" ? { status: filter } : {} }),
        api.get("/leads/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (user && user.email) load();
  }, [user, load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked ${status}`);
      api.get("/leads/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch {
      toast.error("Update failed");
    }
  };

  const downloadFile = (f) => {
    const url = `${API_BASE}/files/${f.storage_path}?auth=${token}`;
    window.open(url, "_blank");
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "creative-prints-leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const filtered = leads.filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (l.name || "").toLowerCase().includes(q) || (l.phone || "").includes(q) || (l.service || "").toLowerCase().includes(q);
  });

  if (!user || !user.email) {
    return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-primary text-white"><Printer size={20} /></span>
            <div>
              <h1 className="font-heading text-base font-extrabold text-slate-900">Lead Dashboard</h1>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/orders" data-testid="orders-link">
              <Button variant="outline" size="sm" className="gap-2">
                <ShoppingBag size={15} /> <span className="hidden sm:inline">Orders</span>
              </Button>
            </Link>
            <Link to="/admin/designs" data-testid="design-quotes-link">
              <Button variant="outline" size="sm" className="gap-2">
                <Palette size={15} /> <span className="hidden sm:inline">Design Quotes</span>
              </Button>
            </Link>
            <Link to="/admin/ai" data-testid="ai-studio-link">
              <Button variant="outline" size="sm" className="gap-2">
                <Sparkles size={15} /> <span className="hidden sm:inline">AI Studio</span>
              </Button>
            </Link>
            <Link to="/admin/content" data-testid="content-editor-link">
              <Button variant="outline" size="sm" className="gap-2">
                <Pencil size={15} /> <span className="hidden sm:inline">Edit Site</span>
              </Button>
            </Link>
            <Link to="/admin/gallery" data-testid="gallery-manager-link">
              <Button variant="outline" size="sm" className="gap-2">
                <Images size={15} /> <span className="hidden sm:inline">Gallery</span>
              </Button>
            </Link>
            <Button data-testid="export-csv-btn" variant="outline" size="sm" className="gap-2" onClick={exportCsv}>
              <FileSpreadsheet size={15} /> <span className="hidden sm:inline">Export CSV</span>
            </Button>
            <Button data-testid="refresh-btn" variant="outline" size="sm" className="gap-2" onClick={load}>
              <RefreshCw size={15} /> <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button data-testid="admin-logout-btn" variant="outline" size="sm" className="gap-2" onClick={() => { logout(); navigate("/admin"); }}>
              <LogOut size={15} /> <span className="hidden sm:inline">Log Out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div data-testid="stat-total" className="rounded-xl border border-slate-200 bg-white p-4 col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 text-slate-500"><Inbox size={16} /><span className="text-xs font-semibold uppercase">Total</span></div>
            <p className="mt-1 text-2xl font-extrabold text-slate-900">{stats?.total ?? "—"}</p>
          </div>
          {["New", "Contacted", "Quoted", "In Production", "Completed"].map((s) => (
            <div key={s} data-testid={`stat-${s}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-slate-500"><TrendingUp size={16} /><span className="text-xs font-semibold truncate">{s}</span></div>
              <p className="mt-1 text-2xl font-extrabold text-slate-900">{stats?.by_status?.[s] ?? "—"}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setFilter(s)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${filter === s ? "bg-primary text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-primary"}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="relative sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone…" className="pl-9" />
          </div>
        </div>

        {/* Leads */}
        <div className="mt-5 space-y-4">
          {loading ? (
            <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div data-testid="no-leads" className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center text-slate-400">
              <Inbox size={40} className="mx-auto" />
              <p className="mt-3 font-medium">No leads found</p>
            </div>
          ) : (
            filtered.map((lead) => (
              <div key={lead.id} data-testid={`lead-card-${lead.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-lg font-bold text-slate-900">{lead.name}</h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLORS[lead.status] || "bg-slate-100 text-slate-600"}`}>{lead.status}</span>
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">{lead.service}</span>
                      <span className="text-xs text-slate-400">{new Date(lead.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                    </div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-600">
                      <p><span className="font-semibold text-slate-500">Phone:</span> {lead.phone}</p>
                      {lead.dimensions && <p><span className="font-semibold text-slate-500">Size:</span> {lead.dimensions}</p>}
                      {lead.quantity && <p><span className="font-semibold text-slate-500">Qty:</span> {lead.quantity}</p>}
                      <p><span className="font-semibold text-slate-500">Install:</span> {lead.installation ? "Yes" : "No"}</p>
                      {lead.delivery_date && <p><span className="font-semibold text-slate-500">Deliver by:</span> {lead.delivery_date}</p>}
                      <p><span className="font-semibold text-slate-500">Source:</span> {lead.source}</p>
                    </div>
                    {lead.message && <p className="mt-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{lead.message}</p>}

                    {lead.files?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {lead.files.map((f) => (
                          <button
                            key={f.id}
                            data-testid={`lead-file-${f.id}`}
                            onClick={() => downloadFile(f)}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-primary hover:text-primary transition-colors"
                          >
                            <FileText size={14} /> <span className="max-w-[140px] truncate">{f.original_filename}</span> <Download size={13} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 lg:w-48 shrink-0">
                    <div className="flex gap-2">
                      <a href={`tel:${lead.phone}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full gap-1.5"><Phone size={14} /> Call</Button>
                      </a>
                      <a href={waLink(`Hi ${lead.name}, thank you for your enquiry with Creative Prints regarding ${lead.service}. Here is your quote:`)} target="_blank" rel="noreferrer" className="flex-1">
                        <Button size="sm" className="w-full gap-1.5 bg-[#25D366] hover:bg-[#1da851] text-white"><MessageCircle size={14} /> Reply</Button>
                      </a>
                    </div>
                    <select
                      data-testid={`lead-status-select-${lead.id}`}
                      value={lead.status}
                      onChange={(e) => updateStatus(lead.id, e.target.value)}
                      className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {STATUSES.filter((s) => s !== "All").map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
